import { defineStore, storeToRefs } from 'pinia';
import { ref } from 'vue';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { useAppLoading } from '@/stores/app/loading';
import { getRequisitions } from '@/api/requisitions';
import { useAppPagination } from '@/stores/app/pagination';
import { useSubMenu } from '@/stores/filters/subMenu';
import { useLibrary } from '@/stores/library/library';
import { getOtherLibraryTypes } from '@/stores/library/helpers';
import { modeMap, getDateSort, getDefaultReqSort } from '@/stores/requisitions/helpers';
import { useFilters } from '@/stores/filters/filters';

export const useRequisitionLibrary = defineStore('requisitionLibrary', () => {
  // Other Stores
  const { addErrorSnackbar } = useAppSnackbar();
  const { setLoading } = useAppLoading();

  // State
  const requisitions = ref([]);
  const total = ref(0);
  const otherRequisitionTypeTotals = ref([]);
  const otherRequisitionTypesLoading = ref(false);
  const sortOptions = ref([]);

  const { selectedSubMenuOption } = storeToRefs(useSubMenu());

  // Mutations
  const updateRequisitions = (newRequisitions) => {
    requisitions.value = newRequisitions;
  };

  const updateTotal = (newTotal) => {
    total.value = newTotal;
  };

  const updateOtherRequisitionTypeTotals = (newTotals) => {
    otherRequisitionTypeTotals.value = newTotals;
  };

  const updateOtherRequisitionTypesLoading = (newLoading) => {
    otherRequisitionTypesLoading.value = newLoading;
  };

  const updateSortOptions = (newSortOptions) => {
    sortOptions.value = newSortOptions;
  };

  // Actions
  const loadRequisitions = async ({
    mode,
    routeQuery,
    selectedFilters,
    page,
    searchTerm: newSearchTerm,
    selectedMyJobs,
  }) => {
    setLoading({ pageLoading: true, listLoading: true });

    const dateSort = getDateSort(mode);
    const defaultSort = getDefaultReqSort(mode);

    try {
      let params = routeQuery || {};

      if (defaultSort && !params.sort && !selectedSubMenuOption.value) {
        params.sort = defaultSort;
      } else if (selectedSubMenuOption.value) {
        params = { ...params, sort: selectedSubMenuOption.value };
      }

      params.page = page || undefined;

      const parameters = {
        mode: modeMap[mode] || mode,
        params,
        selectedMyJobs,
        searchTerm: newSearchTerm,
        selectedFilters,
      };

      const libraryRequisitions = await getRequisitions(parameters);

      const { sorting } = libraryRequisitions;
      sorting.value = sorting?.value || dateSort;

      updateSortOptions(sorting.options.map(({ id }) => id));

      updateRequisitions(libraryRequisitions.documents);
      useAppPagination().updatePagination(libraryRequisitions.pagination);
      updateTotal(libraryRequisitions.total);

      if (!libraryRequisitions.documents?.length) {
        getOtherLibraryTypes({
          ...parameters,
          updateTotals: updateOtherRequisitionTypeTotals,
          updateLoading: updateOtherRequisitionTypesLoading,
          menuPages: useLibrary().menuPages,
        });
      }

      if (libraryRequisitions.textFilter?.value) {
        useFilters().setSearchTerm(libraryRequisitions.textFilter.value);
      }
    } catch (error) {
      addErrorSnackbar({ text: `Load requisitions - ${error.message}` });
      throw error;
    } finally {
      setLoading({ pageLoading: false, sidebarLoading: false, listLoading: false });
    }
  };

  return {
    // State
    requisitions,
    total,
    otherRequisitionTypeTotals,
    otherRequisitionTypesLoading,
    sortOptions,

    // Mutations
    updateRequisitions,

    // Actions
    loadRequisitions,
  };
});
